/** One schedule row from `GET /schedules/:id/children/dropdown`. */
export interface DropdownSchedule {
  id: string
  starts_at?: string | null
  /** A division carries its season here, with its own `starts_at`. */
  parent?: DropdownSchedule | null
}

/** One level of the dropdown — "Season", "Division", "Team", … */
export interface DropdownGroup {
  name?: string
  schedules?: DropdownSchedule[] | null
}

/**
 * The `starts_at` of a schedule in a competition's children dropdown, by id.
 *
 * The team screen keeps only the selected season's **id** in its filter state,
 * so the date has to be read back out of the dropdown payload the filters were
 * built from. Nested `parent` entries are searched too: a season appears there
 * on each of its divisions, and on some payloads only there.
 *
 * Returns `null` when the id is absent or the row carries no date — callers
 * pass the result to `resolvePastWindowStart`, which falls back to its floor.
 */
export const findScheduleStartsAt = (
  groups: DropdownGroup[] | null | undefined,
  scheduleId: string | null | undefined
): string | null => {
  if (!Array.isArray(groups) || !scheduleId) {
    return null
  }

  for (const group of groups) {
    const schedules = group?.schedules
    if (!Array.isArray(schedules)) {
      continue
    }

    for (const schedule of schedules) {
      if (schedule?.id === scheduleId) {
        return schedule.starts_at ?? null
      }
      if (schedule?.parent?.id === scheduleId) {
        return schedule.parent.starts_at ?? null
      }
    }
  }

  return null
}

export default findScheduleStartsAt
